import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import Logo from './Logo';
import { NAV_LINKS } from '../../lib/constants';
import { useAuth } from '../../hooks/useAuth';

const Header: React.FC = () => {
  const { authed, username, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isPublic = !location.pathname.startsWith('/app');

  const handleLogout = () => {
    setDrawerOpen(false);
    logout();
    navigate('/login', { replace: true });
  };

  // Close drawer on route change.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Body scroll-lock + Esc to close while drawer is open.
  // Uses overflowY specifically — html handles overflow-x globally, so
  // setting the shorthand `overflow` would redundantly also lock X on body.
  useEffect(() => {
    if (!drawerOpen) return;
    const prev = document.body.style.overflowY;
    document.body.style.overflowY = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflowY = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [drawerOpen]);

  const navLinks = authed
    ? NAV_LINKS
    : [
        { to: '/',        label: 'Home',    end: true },
        { to: '/pricing', label: 'Pricing', end: false },
        { to: '/faq',     label: 'FAQ',     end: false },
      ];

  // The drawer MUST be rendered OUTSIDE the <header> element.
  //
  // Root cause of prior "drawer doesn't open" bug: <header> has
  // `backdrop-filter: blur(18px)` in globals.css. Per the CSS spec
  // (Filter Effects Level 2), any element with backdrop-filter ≠ none
  // establishes a new containing block for position:fixed descendants.
  // This means a position:fixed child of <header> is positioned and
  // clipped relative to the header box (~52px tall) rather than the
  // viewport — the drawer renders, transforms to translateY(0), but
  // is invisible because it's clipped to the 52px header boundary.
  //
  // Fix: return a Fragment so the drawer is a sibling of <header> in
  // the DOM. Both are children of .gv-shell (no backdrop-filter), so
  // position:fixed works correctly relative to the viewport.
  return (
    <>
      <header className="gv-header" data-testid="gv-header">
        <Link to={authed ? '/app/options' : '/'} className="gv-logo" aria-label="GammaVision home">
          <Logo title="GammaVision" />
          <span className="gv-logo-text">GAMMAVISION</span>
        </Link>

        <nav className="gv-nav" aria-label="Primary">
          {navLinks.map(link => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) => (isActive ? 'active' : undefined)}
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="gv-header-right">
          {authed ? (
            <>
              {username && (
                <span
                  className="gv-header-user"
                  title={`Signed in as ${username}`}
                >
                  {username}
                </span>
              )}
              <button type="button" className="gv-logout" onClick={handleLogout}>
                LOGOUT
              </button>
            </>
          ) : isPublic ? (
            <Link to="/login" className="gv-login-cta">SIGN IN</Link>
          ) : null}

          <button
            type="button"
            className={`gv-hamburger${drawerOpen ? ' open' : ''}`}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={drawerOpen}
            aria-controls="gv-mobile-drawer"
            onClick={() => setDrawerOpen(prev => !prev)}
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      {/* Drawer is a sibling of <header>, NOT a child — see comment above.
          position:fixed works correctly here because .gv-shell (the parent)
          has no backdrop-filter, transform, or will-change that would
          create a containing block for fixed-position descendants. */}
      <div
        id="gv-mobile-drawer"
        className={`gv-mobile-drawer${drawerOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        aria-hidden={!drawerOpen}
        // `inert` removes the subtree from the tab order and AT entirely
        // when the drawer is closed. The string form is required because
        // React 18 doesn't yet type `inert` as a boolean attribute.
        // tabIndex=-1 on NavLinks below is a belt-and-suspenders fallback
        // for browsers that don't yet support `inert`.
        {...(!drawerOpen ? { inert: '' } : {})}
      >
        {navLinks.map(link => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.end}
            className={({ isActive }) => (isActive ? 'active' : undefined)}
            tabIndex={drawerOpen ? undefined : -1}
            onClick={() => setDrawerOpen(false)}
          >
            {link.label}
          </NavLink>
        ))}
        {/* Spacer pushes auth action to the bottom of the drawer list */}
        <div className="gv-drawer-spacer" aria-hidden="true" />
        {authed && (
          <button
            type="button"
            className="gv-logout"
            tabIndex={drawerOpen ? undefined : -1}
            onClick={handleLogout}
          >
            LOGOUT
          </button>
        )}
        {!authed && isPublic && (
          <Link
            to="/login"
            className="gv-login-cta"
            tabIndex={drawerOpen ? undefined : -1}
            onClick={() => setDrawerOpen(false)}
          >
            SIGN IN
          </Link>
        )}
      </div>
    </>
  );
};

export default Header;
